import { useState } from "react";
import { Link } from "react-router-dom";
import {
  decideChangeApproval,
  decideKbApproval,
  decideTaskApproval,
  decideTicketApproval,
  listMyTicketApprovals,
} from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import { formatDate } from "../lib/format";
import { PageBar } from "../components/Layout";
import { Alert, EmptyState, ErrorState, Loading } from "../components/States";

type ApprovalTab = "tickets" | "tasks" | "kb" | "change";

export function AccessRequestsPage() {
  const ticketApprovals = useApi((signal) => listMyTicketApprovals(signal), []);
  const approvalMut = useMutation((id: string, approve: boolean) => decideTicketApproval(id, { approve }));
  const taskApprovalMut = useMutation((id: string, approve: boolean) => decideTaskApproval(id, { approve }));
  const kbApprovalMut = useMutation((id: string, approve: boolean) => decideKbApproval(id, { approve }));
  const changeApprovalMut = useMutation((requestId: string, approve: boolean) =>
    decideChangeApproval(requestId, { decision: approve ? "approved" : "rejected" }),
  );

  const approvalRows = ticketApprovals.data?.approvals ?? [];
  const taskRows = ticketApprovals.data?.taskApprovals ?? [];
  const kbRows = ticketApprovals.data?.kbApprovals ?? [];
  const changeRows = ticketApprovals.data?.changeApprovals ?? [];

  const [tab, setTab] = useState<ApprovalTab>("tickets");
  const TABS: { key: ApprovalTab; label: string; count: number }[] = [
    { key: "tickets", label: "Tickets", count: approvalRows.length },
    { key: "tasks", label: "Action items", count: taskRows.length },
    { key: "kb", label: "KB articles", count: kbRows.length },
    { key: "change", label: "Change requests", count: changeRows.length },
  ];

  async function decideApproval(id: string, approve: boolean) {
    const res = await approvalMut.run(id, approve);
    if (res) ticketApprovals.reload();
  }
  async function decideTask(id: string, approve: boolean) {
    const res = await taskApprovalMut.run(id, approve);
    if (res) ticketApprovals.reload();
  }
  async function decideKb(id: string, approve: boolean) {
    const res = await kbApprovalMut.run(id, approve);
    if (res) ticketApprovals.reload();
  }
  async function decideChange(requestId: string, approve: boolean) {
    const res = await changeApprovalMut.run(requestId, approve);
    if (res) ticketApprovals.reload();
  }

  return (
    <>
      <PageBar crumbs="Approvals" />
      <div className="content">
        <div className="page-head">
          <div>
            <h1>Approvals</h1>
            <div className="sub">Review and decide on items awaiting your approval.</div>
          </div>
          <button className="btn small" onClick={() => ticketApprovals.reload()}>
            Refresh
          </button>
        </div>

        {approvalMut.error ? <Alert kind="error">{approvalMut.error}</Alert> : null}
        {taskApprovalMut.error ? <Alert kind="error">{taskApprovalMut.error}</Alert> : null}
        {kbApprovalMut.error ? <Alert kind="error">{kbApprovalMut.error}</Alert> : null}
        {changeApprovalMut.error ? <Alert kind="error">{changeApprovalMut.error}</Alert> : null}

        <div className="tabs" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key}
              role="tab"
              aria-selected={tab === t.key}
              className={`tab${tab === t.key ? " active" : ""}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
              {t.count > 0 ? <span className="tab-count">{t.count}</span> : null}
            </button>
          ))}
        </div>

        {ticketApprovals.loading ? (
          <Loading label="Loading approvals…" />
        ) : ticketApprovals.error ? (
          <ErrorState error={ticketApprovals.error} onRetry={ticketApprovals.reload} />
        ) : tab === "tickets" ? (
          approvalRows.length === 0 ? (
            <EmptyState>No ticket approvals waiting on you.</EmptyState>
          ) : (
            <table className="data">
              <thead>
                <tr>
                  <th>Ticket</th>
                  <th>Requested by</th>
                  <th>Requested</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {approvalRows.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <Link to={`/tickets/${a.ticket_id}`}>
                        {a.ticket_number != null ? `#${a.ticket_number} ` : ""}
                        {a.ticket_subject}
                      </Link>
                      {a.note ? <div className="muted">“{a.note}”</div> : null}
                    </td>
                    <td>{a.requested_by_name || "—"}</td>
                    <td>{formatDate(a.created_at)}</td>
                    <td>
                      <div className="btn-row">
                        <button
                          className="btn small primary"
                          onClick={() => decideApproval(a.id, true)}
                          disabled={approvalMut.pending}
                        >
                          Approve
                        </button>
                        <button
                          className="btn small"
                          onClick={() => decideApproval(a.id, false)}
                          disabled={approvalMut.pending}
                        >
                          Deny
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : tab === "tasks" ? (
          taskRows.length === 0 ? (
            <EmptyState>No action item approvals waiting on you.</EmptyState>
          ) : (
            <table className="data">
              <thead>
                <tr>
                  <th>Action item</th>
                  <th>Requested by</th>
                  <th>Requested</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {taskRows.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <Link to={`/action-items?open=${a.task_id}`}>{a.task_title}</Link>
                      {a.note ? <div className="muted">“{a.note}”</div> : null}
                    </td>
                    <td>{a.requested_by_name || "—"}</td>
                    <td>{formatDate(a.created_at)}</td>
                    <td>
                      <div className="btn-row">
                        <button
                          className="btn small primary"
                          onClick={() => decideTask(a.id, true)}
                          disabled={taskApprovalMut.pending}
                        >
                          Approve
                        </button>
                        <button
                          className="btn small"
                          onClick={() => decideTask(a.id, false)}
                          disabled={taskApprovalMut.pending}
                        >
                          Deny
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : tab === "kb" ? (
          kbRows.length === 0 ? (
            <EmptyState>No KB articles waiting on your review.</EmptyState>
          ) : (
            <table className="data">
              <thead>
                <tr>
                  <th>Article</th>
                  <th>Requested by</th>
                  <th>Requested</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {kbRows.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <Link to={`/kb?open=${a.article_id}`}>{a.article_title}</Link>
                      {a.note ? <div className="muted">“{a.note}”</div> : null}
                    </td>
                    <td>{a.requested_by_name || "—"}</td>
                    <td>{formatDate(a.created_at)}</td>
                    <td>
                      <div className="btn-row">
                        <button
                          className="btn small primary"
                          onClick={() => decideKb(a.id, true)}
                          disabled={kbApprovalMut.pending}
                        >
                          Approve
                        </button>
                        <button
                          className="btn small"
                          onClick={() => decideKb(a.id, false)}
                          disabled={kbApprovalMut.pending}
                        >
                          Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : changeRows.length === 0 ? (
          <EmptyState>No change requests waiting on your approval.</EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Change</th>
                <th>Requested by</th>
                <th>Requested</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {changeRows.map((a) => (
                <tr key={a.id}>
                  <td>
                    <Link to={`/change-management/${a.request_id}`}>
                      #{a.number} {a.title}
                    </Link>
                  </td>
                  <td>{a.requested_by_name || "—"}</td>
                  <td>{formatDate(a.created_at)}</td>
                  <td>
                    <div className="btn-row">
                      <button
                        className="btn small primary"
                        onClick={() => decideChange(a.request_id, true)}
                        disabled={changeApprovalMut.pending}
                      >
                        Approve
                      </button>
                      <button
                        className="btn small"
                        onClick={() => decideChange(a.request_id, false)}
                        disabled={changeApprovalMut.pending}
                      >
                        Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
